import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { AuthorizedButton } from '@/components/ui/AuthorizedButton'
import { usePermissions } from '@/hooks/use-permissions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Loader2, ChevronDown, ChevronUp, Plus, X, TestTube, Crown } from 'lucide-react'
import { usePools, useCreatePool } from '@/hooks/use-pools'
import { useInfiniteListAccounts } from '@/hooks/use-accounts'
import { useToast } from '@/hooks/use-toast'
import { FORECAST_PURPOSES } from '@/lib/entity-icons'
import { FALLBACK_BEHAVIOR_DESCRIPTIONS } from '@/lib/pool-control'

const controlPurposeDescriptions: Record<string, string> = {
  imbalance:
    'Asset: Batteries connected via gridBox gateway. Market: Passive imbalance market — a zero-power default task is always active; a PID controller tracks a continuous power setpoint.',
  intraday:
    'Asset: EV charging stations (EVCS) connected via gridBox gateway. Market: Intraday flex — charging is scheduled in 15-minute intervals; the pool is idle when no task is active.',
  intraday_cloud:
    'Asset: Cloud-connected EVs (e.g., Enode). Connection: Cloud API (c2c), not gateway. Market: Intraday flex — binary charge/stop commands; fallback behavior controls what happens when no task is scheduled.',
  tou_stacking_poc:
    'Asset: Batteries via gridBox. Market: Value stacking — combines intraday flex with Time-of-Use pricing optimization. Pool is idle when no task is active.',
}

interface CreatePoolDialogProps {
  open: boolean
  onClose: () => void
}

export function CreatePoolDialog({ open, onClose }: CreatePoolDialogProps) {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { data: pools = [] } = usePools()
  const { mutateAsync: createPool } = useCreatePool()
  const { toast } = useToast()

  // Account combobox data
  const { data: accountPages, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteListAccounts()
  const allAccounts = useMemo(
    () => accountPages?.pages.flatMap((p) => p.accounts) ?? [],
    [accountPages]
  )
  const accountsMap = useMemo(
    () => Object.fromEntries(allAccounts.map((a) => [a.id, a])),
    [allAccounts]
  )
  const orderedAccounts = useMemo(() => {
    const known = new Set(allAccounts.map((a) => a.id))
    const roots = allAccounts.filter(
      (a) => !a.parentID || !known.has(a.parentID)
    )
    const result: { id: string; depth: number }[] = []
    const visit = (id: string, depth: number) => {
      result.push({ id, depth })
      allAccounts
        .filter((a) => a.parentID === id)
        .forEach((a) => visit(a.id, depth + 1))
    }
    roots.forEach((a) => visit(a.id, 0))
    return result
  }, [allAccounts])

  // Form state
  const [name, setName] = useState('')
  const [accountID, setAccountID] = useState('')
  const [accountInputValue, setAccountInputValue] = useState('')
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false)
  const [poolType, setPoolType] = useState<'control' | 'forecast' | 'both'>('control')
  const [controlPurpose, setControlPurpose] = useState<
    'imbalance' | 'intraday' | 'intraday_cloud' | 'tou_stacking_poc'
  >('imbalance')
  const [dryRun, setDryRun] = useState(false)
  const [multiTargetAsset, setMultiTargetAsset] = useState(false)
  const [fallbackBehavior, setFallbackBehavior] = useState<'none' | 'last_possible_moment' | 'time_of_use'>('last_possible_moment')
  const [isDefault, setIsDefault] = useState(false)
  type ForecastEntry = { purpose: string; isActive: boolean }
  const [forecastEntries, setForecastEntries] = useState<ForecastEntry[]>([])
  const [tags, setTags] = useState<{ key: string; value: string }[]>([])
  const [showTags, setShowTags] = useState(false)
  const [loading, setLoading] = useState(false)

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  const filteredAccounts = orderedAccounts.filter(({ id }) => {
    const accountName = accountsMap[id]?.name?.toLowerCase() ?? ''
    const search = accountInputValue.toLowerCase()
    return !search || accountName.includes(search) || id.toLowerCase().startsWith(search)
  })

  const handleAccountInput = (val: string) => {
    setAccountInputValue(val)
    setAccountID(uuidRegex.test(val.trim()) ? val.trim() : '')
    setAccountDropdownOpen(true)
  }

  const selectAccount = (id: string) => {
    setAccountID(id)
    setAccountInputValue(accountsMap[id]?.name ?? id)
    setAccountDropdownOpen(false)
  }

  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (!isFetchingNextPage && hasNextPage && el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      fetchNextPage()
    }
  }

  const validateForm = (): string | null => {
    if (!name.trim()) return 'Pool name is required'
    if (!accountID.trim()) return 'Account ID is required'
    if (!uuidRegex.test(accountID.trim())) return 'Account ID must be a valid UUID'

    const duplicatePool = pools.find(
      (pool) => pool.name.toLowerCase() === name.trim().toLowerCase()
    )
    if (duplicatePool) {
      return `A pool named "${name.trim()}" already exists. Please choose a different name.`
    }

    if ((poolType === 'control' || poolType === 'both') && !controlPurpose) {
      return 'Control purpose is required'
    }

    if ((poolType === 'forecast' || poolType === 'both') && forecastEntries.length === 0) {
      return 'At least one forecast purpose is required'
    }

    const tagKeys = tags.map((t) => t.key.trim()).filter((k) => k)
    if (tagKeys.length !== new Set(tagKeys).size) return 'Tag keys must be unique'

    for (const tag of tags) {
      if (tag.value.trim() && !tag.key.trim()) return 'Tag keys cannot be empty'
    }

    return null
  }

  const handleSubmit = async () => {
    const validationError = validateForm()
    if (validationError) {
      toast({ title: 'Validation Error', description: validationError, variant: 'destructive' })
      return
    }

    setLoading(true)
    try {
      const requestBody: any = { accountID: accountID.trim(), name: name.trim() }

      if (isDefault) requestBody.isDefault = true

      if (poolType === 'control' || poolType === 'both') {
        requestBody.controlConfig = {
          purpose: controlPurpose,
          dryRun,
          multiTargetAsset,
          ...(controlPurpose === 'intraday_cloud' && { fallbackBehavior }),
        }
      }

      if (poolType === 'forecast' || poolType === 'both') {
        requestBody.forecastConfigs = forecastEntries.map(({ purpose, isActive }) => ({ purpose, isActive }))
      }

      const validTags = tags.filter((t) => t.key.trim() && t.value.trim())
      if (validTags.length > 0) {
        requestBody.tags = validTags.reduce(
          (acc, tag) => { acc[tag.key.trim()] = tag.value.trim(); return acc },
          {} as Record<string, string>
        )
      }

      const newPool = await createPool(requestBody)
      toast({ title: 'Success', description: 'Pool created successfully' })
      navigate(`/pools/${newPool.poolID}`)
      handleClose()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create pool',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setName('')
      setAccountID('')
      setAccountInputValue('')
      setAccountDropdownOpen(false)
      setPoolType('control')
      setControlPurpose('imbalance')
      setDryRun(false)
      setMultiTargetAsset(false)
      setFallbackBehavior('last_possible_moment')
      setIsDefault(false)
      setForecastEntries([])
      setTags([])
      setShowTags(false)
      onClose()
    }
  }

  const toggleForecastPurpose = (purpose: string) => {
    setForecastEntries((prev) => {
      const exists = prev.find((e) => e.purpose === purpose)
      if (exists) return prev.filter((e) => e.purpose !== purpose)
      return [...prev, { purpose, isActive: true }]
    })
  }

  const toggleForecastActive = (purpose: string) => {
    setForecastEntries((prev) =>
      prev.map((e) => e.purpose === purpose ? { ...e, isActive: !e.isActive } : e)
    )
  }

  const addTag = () => setTags([...tags, { key: '', value: '' }])
  const removeTag = (index: number) => setTags(tags.filter((_, i) => i !== index))
  const updateTag = (index: number, field: 'key' | 'value', value: string) => {
    const newTags = [...tags]
    newTags[index][field] = value
    setTags(newTags)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Create Pool</CardTitle>
          <CardDescription>Create a new flexibility pool</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pool Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Pool Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="e.g., Production Imbalance Pool"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Account ID */}
          <div className="space-y-2">
            <Label htmlFor="accountID">
              Account ID <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="accountID"
                placeholder="Search accounts or enter UUID…"
                value={accountInputValue}
                onChange={(e) => handleAccountInput(e.target.value)}
                onFocus={() => setAccountDropdownOpen(true)}
                onBlur={() => setTimeout(() => setAccountDropdownOpen(false), 150)}
                disabled={loading}
                autoComplete="off"
                className={accountInputValue ? 'pr-8' : ''}
              />
              {accountInputValue && !loading && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onMouseDown={(e) => { e.preventDefault(); setAccountInputValue(''); setAccountID(''); setAccountDropdownOpen(false) }}
                  tabIndex={-1}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {accountDropdownOpen && filteredAccounts.length > 0 && (
                <div
                  className="absolute z-50 top-full left-0 right-0 mt-1 max-h-60 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
                  onScroll={handleDropdownScroll}
                >
                  {filteredAccounts.map(({ id, depth }) => {
                    const account = accountsMap[id]
                    return (
                      <div
                        key={id}
                        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                        style={{ paddingLeft: `${depth * 16 + 12}px` }}
                        onMouseDown={() => selectAccount(id)}
                      >
                        {depth > 0 && <span className="text-muted-foreground text-xs">└─</span>}
                        <span className="font-medium truncate">{account?.name ?? id}</span>
                        <span className="ml-auto text-xs text-muted-foreground font-mono shrink-0">{id.slice(0, 8)}…</span>
                      </div>
                    )
                  })}
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              The account where this pool will be created.
            </p>
          </div>

          {/* Pool Type */}
          <div className="space-y-2">
            <Label>
              Pool Type <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={poolType}
              onValueChange={(v: string) => setPoolType(v as typeof poolType)}
              disabled={loading}
            >
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="control" id="poolType-control" className="mt-0.5" />
                <Label htmlFor="poolType-control" className="cursor-pointer font-normal">
                  <span className="font-medium">Control Pool</span>
                  <p className="text-xs text-muted-foreground font-normal">Sends setpoints to assets on a schedule</p>
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="forecast" id="poolType-forecast" className="mt-0.5" />
                <Label htmlFor="poolType-forecast" className="cursor-pointer font-normal">
                  <span className="font-medium">Forecast Pool</span>
                  <p className="text-xs text-muted-foreground font-normal">Provides flex-band forecasts for scheduling</p>
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="both" id="poolType-both" className="mt-0.5" />
                <Label htmlFor="poolType-both" className="cursor-pointer font-normal">
                  <span className="font-medium">Both (Control + Forecast)</span>
                  <p className="text-xs text-muted-foreground font-normal">Combines control and forecasting in one pool</p>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Default Pool */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="isDefault"
              checked={isDefault}
              onCheckedChange={(checked: boolean | 'indeterminate') => setIsDefault(checked === true)}
              disabled={loading}
              className="mt-1"
            />
            <div className="flex-1">
              <Label htmlFor="isDefault" className="cursor-pointer flex items-center gap-2">
                <Crown className="h-4 w-4" />
                Default Pool
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                New systems in this account will automatically be added to the default pool
              </p>
            </div>
          </div>

          {/* Control Configuration */}
          {(poolType === 'control' || poolType === 'both') && (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
              <h3 className="font-medium text-sm">Control Configuration</h3>

              <div className="space-y-2">
                <Label htmlFor="controlPurpose">
                  Purpose <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={controlPurpose}
                  onValueChange={(v) => setControlPurpose(v as typeof controlPurpose)}
                  disabled={loading}
                >
                  <SelectTrigger id="controlPurpose">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="imbalance">Imbalance</SelectItem>
                    <SelectItem value="intraday">Intraday</SelectItem>
                    <SelectItem value="intraday_cloud">Intraday Cloud</SelectItem>
                    <SelectItem value="tou_stacking_poc">TOU Stacking POC</SelectItem>
                  </SelectContent>
                </Select>
                {controlPurposeDescriptions[controlPurpose] && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {controlPurposeDescriptions[controlPurpose]}
                  </p>
                )}
              </div>

              {controlPurpose === 'intraday_cloud' && (
                <div className="space-y-2">
                  <Label htmlFor="fallbackBehavior">Fallback Behavior</Label>
                  <Select
                    value={fallbackBehavior}
                    onValueChange={(v) => setFallbackBehavior(v as typeof fallbackBehavior)}
                    disabled={loading}
                  >
                    <SelectTrigger id="fallbackBehavior">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="last_possible_moment">Last possible moment</SelectItem>
                      <SelectItem value="time_of_use">Time of use</SelectItem>
                    </SelectContent>
                  </Select>
                  {FALLBACK_BEHAVIOR_DESCRIPTIONS[fallbackBehavior] && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {FALLBACK_BEHAVIOR_DESCRIPTIONS[fallbackBehavior]}
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="dryRun"
                  checked={dryRun}
                  onCheckedChange={(checked: boolean | 'indeterminate') => setDryRun(checked === true)}
                  disabled={loading}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="dryRun" className="cursor-pointer flex items-center gap-2">
                    <TestTube className="h-4 w-4" />
                    Dry Run Mode
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Commands are logged but not executed (for testing)
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="multiTargetAsset"
                  checked={multiTargetAsset}
                  onCheckedChange={(checked: boolean | 'indeterminate') => setMultiTargetAsset(checked === true)}
                  disabled={loading}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="multiTargetAsset" className="cursor-pointer">
                    Multi-Target Asset
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pool targets multiple assets simultaneously
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Forecast Configuration */}
          {(poolType === 'forecast' || poolType === 'both') && (
            <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
              <h3 className="font-medium text-sm">
                Forecast Configuration <span className="text-red-500">*</span>
              </h3>
              <p className="text-xs text-muted-foreground">
                Check to add a forecast purpose. Use the toggle to activate or deactivate it without removing it.
              </p>
              <div className="space-y-2">
                {FORECAST_PURPOSES.map(({ value, label, description }) => {
                  const entry = forecastEntries.find((e) => e.purpose === value)
                  const isIncluded = !!entry
                  return (
                    <div key={value} className="flex items-start gap-3">
                      <Checkbox
                        id={`create-forecast-${value}`}
                        checked={isIncluded}
                        onCheckedChange={() => toggleForecastPurpose(value)}
                        disabled={loading}
                        className="mt-1 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <Label htmlFor={`create-forecast-${value}`} className="text-sm cursor-pointer font-normal">
                          {label}
                        </Label>
                        <p className="text-xs text-muted-foreground">{description}</p>
                      </div>
                      {isIncluded && (
                        <Switch
                          checked={entry.isActive}
                          onCheckedChange={() => toggleForecastActive(value)}
                          disabled={loading}
                          aria-label={`${label} active`}
                          className="shrink-0 mt-0.5"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Tags Section */}
          <Collapsible open={showTags} onOpenChange={setShowTags}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                className="w-full justify-between"
              >
                <span>Tags (Optional)</span>
                {showTags ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-2 p-4 border rounded-lg mt-2">
                {tags.map((tag, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="Key"
                      value={tag.key}
                      onChange={(e) => updateTag(index, 'key', e.target.value)}
                      disabled={loading}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Value"
                      value={tag.value}
                      onChange={(e) => updateTag(index, 'value', e.target.value)}
                      disabled={loading}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeTag(index)}
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addTag}
                  disabled={loading}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tag
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <AuthorizedButton authorized={can('PoolsWrite')} onClick={handleSubmit} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Pool
            </AuthorizedButton>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
